import React from "react";
import { Button, Card, Col, InputNumber, Layout, Row, Space, Typography } from "antd";
import {
  CreditCardOutlined,
  DollarOutlined,
  GiftOutlined,
  KeyOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import {
  goToScreen,
  selectCheckoutTotals,
  selectPosFlow,
  setAmountDue,
  setSelectedPayment,
  type PaymentId,
} from "../store/posSlice";
import { ComingSoonTag } from "../utils/comingSoon";

const { Content } = Layout;
const { Title, Text } = Typography;

function formatMoney(n: number) {
  return n.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

const options: Array<{
  id: PaymentId;
  label: string;
  description: string;
  icon: React.ReactNode;
  // Card methods only simulate authorization until a card processor is connected,
  // so they're shown but can't be selected.
  comingSoon?: boolean;
}> = [
  { id: "card", label: "Card", description: "Tap, chip, or swipe", icon: <CreditCardOutlined />, comingSoon: true },
  { id: "cash", label: "Cash", description: "Pay with cash", icon: <DollarOutlined /> },
  { id: "gift-card", label: "Gift Card", description: "Redeem gift card", icon: <GiftOutlined /> },
  { id: "card-on-file", label: "Card on File", description: "Use saved card", icon: <WalletOutlined />, comingSoon: true },
  { id: "manual-entry", label: "Manual Card Entry", description: "Enter card manually", icon: <KeyOutlined />, comingSoon: true },
];

export function PaymentMethodScreen() {
  const dispatch = useAppDispatch();
  const { amountDue, selectedPayment } = useAppSelector(selectPosFlow);
  const { tax, total, taxRate } = useAppSelector(selectCheckoutTotals);

  const canContinue = selectedPayment !== null;

  return (
    <Layout style={{ minHeight: "100vh", background: "#f5f5f5" }}>
      <Content style={{ padding: 24 }}>
        <div style={{ maxWidth: 980, margin: "0 auto" }}>
          <div style={{ marginBottom: 20, textAlign: "center", paddingTop: 18 }}>
          <Text type="secondary">Amount Due</Text>

          {/* Big formatted display — total including tax */}
          <Title style={{ marginTop: 6, marginBottom: 8, fontSize: 64, lineHeight: 1.05 }}>
            {formatMoney(total)}
          </Title>

          {/* Editable subtotal input */}
          <div style={{ display: "flex", justifyContent: "center" }}>
            <InputNumber
              value={amountDue}
              onChange={(v) => dispatch(setAmountDue(typeof v === "number" ? v : 0))}
              min={0}
              step={0.01}
              precision={2}
              controls
              prefix={<span style={{ color: "rgba(0,0,0,0.35)", fontWeight: 500 }}>$</span>}
              size="large"
              style={{
                width: 260,
                borderRadius: 12,
              }}
            />
          </div>

          {taxRate > 0 && (
            <div style={{ marginTop: 10, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
              <Text type="secondary">Subtotal: {formatMoney(amountDue)}</Text>
              <Text type="secondary">Tax ({(taxRate * 100).toFixed(1)}%): {formatMoney(tax)}</Text>
            </div>
          )}
        </div>

          <Title level={4} style={{ marginBottom: 12 }}>
            Select Payment Method
          </Title>

          <Row gutter={[16, 16]}>
            {options.map((o) => {
              const selected = selectedPayment === o.id;
              return (
                <Col key={o.id} xs={24} sm={12} lg={8}>
                  <Card
                    hoverable={!o.comingSoon}
                    onClick={() => { if (!o.comingSoon) dispatch(setSelectedPayment(o.id)); }}
                    aria-disabled={o.comingSoon}
                    style={{
                      opacity: o.comingSoon ? 0.5 : 1,
                      cursor: o.comingSoon ? "not-allowed" : "pointer",
                      borderRadius: 14,
                      border: selected ? "2px solid #1677ff" : "1px solid #f0f0f0",
                      background: selected ? "#e6f4ff" : "#fff",
                    }}
                  >
                    <Space direction="vertical" size={8}>
                      <div
                        style={{
                          width: 44,
                          height: 44,
                          borderRadius: 12,
                          background: selected ? "#bae0ff" : "#f5f5f5",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 22,
                        }}
                      >
                        {o.icon}
                      </div>

                      <div>
                        <Title level={5} style={{ marginBottom: 2 }}>
                          {o.label}
                          {o.comingSoon && <ComingSoonTag />}
                        </Title>
                        <Text type="secondary">{o.description}</Text>
                      </div>
                    </Space>
                  </Card>
                </Col>
              );
            })}
          </Row>

          <div style={{ marginTop: 18, display: "flex", gap: 12 }}>
            <Button
              type="primary"
              size="large"
              style={{ flex: 1, height: 54, borderRadius: 12, fontWeight: 600 }}
              disabled={!canContinue}
              onClick={() => selectedPayment && dispatch(goToScreen(selectedPayment))}
            >
              Continue to Payment
            </Button>

            <Button size="large" style={{ height: 54, borderRadius: 12, fontWeight: 600 }}>
              Cancel
            </Button>
          </div>
        </div>
      </Content>
    </Layout>
  );
}